"use client"

import { useEffect, useState, useRef } from "react"
import { useNavigate, useSearchParams, useParams, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { addDays, format, isBefore, isAfter } from "date-fns"

import { SearchParams } from "../types/search-params"
import { Button } from "./ui/button"
import { Calendar } from "./ui/calendar"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover"
import { Separator } from "./ui/separator"
import { popularLocations as popularLocationsStatic } from "../data/popular-locations"
import { getPopularCities, searchCities, type City } from "../services/citiesService"
import { CheckIcon } from "./icons/check"
import { SearchIcon } from "./icons/search"
import { SelectorIcon } from "./icons/selector"
import { MapPin } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"

import { cn, constructUrlWithParams } from "../lib/utils"
import { Location } from "../data/locations"
import { generateLocationUrl, extractLocationFromUrl } from "../utils/seoUrls"
import { supabase } from "../lib/supabase"
import { Input } from "./ui/input"
import { Search } from "lucide-react"

interface SearchPanelProps {
  locations: Location[]
  mode?: 'cars' | 'agencies'
  isMobile?: boolean
  onSearchSubmit?: (searchData: {
    location: string
    checkInDate?: Date
    checkOutDate?: Date
    selectedAgency?: string
  }) => void
  // Props to reset internal state
  resetLocation?: boolean
  resetDates?: boolean
  resetAgency?: boolean
}

export function SearchPanel({ locations, mode = 'cars', isMobile = false, onSearchSubmit, resetLocation, resetDates, resetAgency }: SearchPanelProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { lang } = useParams<{ lang: string }>()
  const [searchParams] = useSearchParams()
  const currentLocation = useLocation()
  const mainSearchInputRef = useRef<HTMLInputElement>(null)

  const [open, setOpen] = useState(false)
  const [agencyOpen, setAgencyOpen] = useState(false)
  const [location, setLocation] = useState("")
  const [locSearchTerm, setLocSearchTerm] = useState("")
  const [selectedAgency, setSelectedAgency] = useState("")
  const [agencies, setAgencies] = useState<Array<{id: string, name: string}>>([])
  const [agencySearchTerm, setAgencySearchTerm] = useState("")
  const [popularCities, setPopularCities] = useState<City[]>([])
  const [citiesLoading, setCitiesLoading] = useState(false)
  const [searchedCities, setSearchedCities] = useState<City[]>([])
  const [citySearchLoading, setCitySearchLoading] = useState(false)

  const [checkInDate, setCheckInDate] = useState<Date>()
  const [checkOutDate, setCheckOutDate] = useState<Date>()
  const [isCheckInPopoverOpen, setIsCheckInPopoverOpen] = useState(false)
  const [isCheckOutPopoverOpen, setIsCheckOutPopoverOpen] = useState(false)

  // New state for mobile inline location selector
  const [isLocationSelectorOpen, setIsLocationSelectorOpen] = useState(false)
  const [isCheckInOpen, setIsCheckInOpen] = useState(false)
  const [isCheckOutOpen, setIsCheckOutOpen] = useState(false)

  // Reset internal state when reset props change
  useEffect(() => {
    if (resetLocation) {
      setLocation("");
      setLocSearchTerm("");
    }
  }, [resetLocation]);

  useEffect(() => {
    if (resetDates) {
      setCheckInDate(undefined);
      setCheckOutDate(undefined);
    }
  }, [resetDates]);

  useEffect(() => {
    if (resetAgency) {
      setSelectedAgency("");
      setAgencySearchTerm("");
    }
  }, [resetAgency]);

  /*
   * 🎯 DYNAMIC CITY SYSTEM - NO MORE MANUAL MAPPINGS NEEDED!
   * 
   * This system now works automatically with ANY city in the database:
   * 
   * 1. ✅ All existing cities work automatically
   * 2. ✅ New cities added to database work immediately  
   * 3. ✅ No need to update cityMappings manually
   * 4. ✅ Supports accents and normalization
   * 5. ✅ Falls back gracefully for any city name
   * 
   * How it works:
   * - findLocationMatch(): Tries static locations first, then uses normalized city name
   * - isCitySelected(): Compares normalized names and checks expected slugs
   * - normalizeText(): Handles accents and case differences automatically
   * 
   * Result: Add any city to database → it works immediately in search! 🚀
   */

  // Fetch agencies for agencies mode
  const fetchAgencies = async () => {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('id, name')
        .order('name')
        .limit(50); // Get more agencies for better search results

      if (error) throw error;
      setAgencies(data || []);
    } catch (error) {
      console.error('Error fetching agencies:', error);
    }
  };

  // Fetch popular cities from database
  const fetchPopularCities = async () => {
    setCitiesLoading(true);
    try {
      const cities = await getPopularCities('Morocco', 6);
      setPopularCities(cities);
    } catch (error) {
      console.error('Error fetching popular cities:', error);
      setPopularCities([]);
    } finally {
      setCitiesLoading(false);
    }
  };

  // Helper function to normalize text for comparison (remove accents, lowercase)
  const normalizeText = (text: string) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .trim();
  };

  // Helper function to find location match - now works with ANY database city!
  const findLocationMatch = (cityName: string) => {
    const normalizedCityName = normalizeText(cityName);
    
    // Try exact match with static locations first
    const exactMatch = locations.find(l => normalizeText(l.name) === normalizedCityName);
    if (exactMatch) return exactMatch.slug;
    
    // Try partial match with static locations
    const partialMatch = locations.find(l => 
      normalizeText(l.name).includes(normalizedCityName) || 
      normalizedCityName.includes(normalizeText(l.name))
    );
    if (partialMatch) return partialMatch.slug;
    
    // For any database city (including new ones), convert to proper URL slug
    // This ensures ALL cities work, even newly added ones!
    return normalizedCityName
      .replace(/[^a-z0-9]+/g, '-') // Replace spaces and special chars with hyphens
      .replace(/-+/g, '-') // Remove multiple consecutive hyphens
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  // Helper function to check if city is selected - now works with ANY database city!
  const isCitySelected = (cityName: string) => {
    const normalizedCityName = normalizeText(cityName);
    
    // If location is empty, nothing is selected
    if (!location || location.trim() === '') {
      return false;
    }
    
    // Check if current location is a static location that matches this city
    const matchingLocation = locations.find(l => l.slug === location);
    if (matchingLocation) {
      const locationName = normalizeText(matchingLocation.name);
      if (locationName === normalizedCityName) {
        return true;
      }
    }
    
    // Check if the location slug matches the expected slug for this city
    const expectedSlug = findLocationMatch(cityName);
    if (location === expectedSlug) {
      return true;
    }
    
    // Also check if the location matches the normalized city name (for backward compatibility)
    const normalizedLocation = normalizeText(location.replace(/-/g, ' ')); // Convert hyphens back to spaces for comparison
    if (normalizedLocation === normalizedCityName) {
      return true;
    }
    
    return false;
  };

  // Search cities from database
  const searchCitiesFromDB = async (query: string) => {
    if (query.length < 1) {
      setSearchedCities([]);
      return;
    }
    
    setCitySearchLoading(true);
    try {
      const results = await searchCities(query, 'Morocco', 10);
      setSearchedCities(results);
    } catch (error) {
      console.error('Error searching cities:', error);
      setSearchedCities([]);
    } finally {
      setCitySearchLoading(false);
    }
  };

  // Search cities when search term changes
  useEffect(() => {
    if (locSearchTerm.trim()) {
      searchCitiesFromDB(locSearchTerm.trim());
    } else {
      setSearchedCities([]);
    }
  }, [locSearchTerm]);

  useEffect(() => {
    if (mode === 'agencies') {
      fetchAgencies();
    }
    // Always fetch popular cities
    fetchPopularCities();
  }, [mode]);

  useEffect(() => {
    const locationParam = searchParams.get(SearchParams.LOCATION)
    const checkinParam = searchParams.get(SearchParams.CHECKIN)
    const checkoutParam = searchParams.get(SearchParams.CHECKOUT)
    const agencyParam = searchParams.get('agency')
    
    // Extract location from URL path (for location-based pages like /location-voiture/marrakech)
    const locationFromPath = extractLocationFromUrl(currentLocation.pathname)

    // Priority: URL path location > search param location
    if (locationFromPath) {
      setLocation(locationFromPath)
    } else if (locationParam) {
      setLocation(locationParam)
    }

    // Set selected agency from URL parameter
    if (agencyParam && agencies.length > 0) {
      const agency = agencies.find(a => a.name === agencyParam)
      if (agency) {
        setSelectedAgency(agency.id)
      }
    }

    const checkInDate = checkinParam ? new Date(checkinParam) : undefined
    const checkOutDate = checkoutParam ? new Date(checkoutParam) : undefined

    if (checkInDate && checkOutDate) {
      if (isBefore(checkInDate, checkOutDate)) {
        setCheckInDate(checkInDate)
        setCheckOutDate(checkOutDate)
      }
    } else {
      if (checkInDate) setCheckInDate(checkInDate)
      if (checkOutDate) setCheckOutDate(checkOutDate)
    }
  }, [searchParams, currentLocation.pathname, agencies])

  function submitForm(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    // If custom submit handler is provided, use it instead of navigation
    if (onSearchSubmit) {
      onSearchSubmit({
        location,
        checkInDate,
        checkOutDate,
        selectedAgency
      });
      return;
    }

    if (mode === 'agencies') {
      // For agencies mode - handle agency search
      if (!location && !selectedAgency) {
        return
      }

      const newParams = new URLSearchParams(searchParams.toString())
      newParams.delete('location')
      newParams.delete('agency')

      if (location) {
        // Try to find by slug first
        let currentLocation = locations.find((loc) => loc.slug === location)
        
        // If not found by slug, try to find by name (case-insensitive)
        if (!currentLocation) {
          currentLocation = locations.find((loc) => 
            normalizeText(loc.name) === normalizeText(location) ||
            normalizeText(loc.slug) === normalizeText(location)
          )
        }
        
        if (currentLocation) {
          newParams.set('location', currentLocation.name)
        } else {
          // Fallback: if location not found in mockLocations, use the location value directly
          // This handles database cities that might not be in mockLocations
          // If it looks like a slug (contains hyphens), convert to readable name
          // Otherwise, use it as-is (might already be a city name)
          const readableName = location.includes('-')
            ? location
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
            : location.charAt(0).toUpperCase() + location.slice(1)
          newParams.set('location', readableName)
        }
      }

      if (selectedAgency) {
        const agency = agencies.find(agency => agency.id === selectedAgency)
        if (agency) {
          newParams.set('agency', agency.name)
        }
      }

      navigate(constructUrlWithParams(`/${lang || 'fr'}/agencies`, newParams))
      return
    }

    // For cars mode - handle car rental search
    if (!location && !checkInDate && !checkOutDate) {
      return
    }

    const newParams = new URLSearchParams(searchParams.toString())

    newParams.delete(SearchParams.LOCATION)
    newParams.delete(SearchParams.CHECKIN)
    newParams.delete(SearchParams.CHECKOUT)
    newParams.delete(SearchParams.LAT)
    newParams.delete(SearchParams.LNG)

    if (location) {
      // Don't include location in query params since it's now in the path
      const currentLocation = locations.find((loc) => loc.slug === location)
      if (currentLocation) {
        newParams.set(SearchParams.LAT, currentLocation.latitude.toString())
        newParams.set(SearchParams.LNG, currentLocation.longitude.toString())
      }
    }

    const checkinISOString = checkInDate?.toISOString()
    if (checkinISOString) newParams.set(SearchParams.CHECKIN, checkinISOString)

    const checkoutISOString = checkOutDate?.toISOString()
    if (checkoutISOString)
      newParams.set(SearchParams.CHECKOUT, checkoutISOString)

    // If location is selected, use SEO-friendly URL
    if (location) {
      const seoUrl = generateLocationUrl(location, (lang as 'fr' | 'en' | 'ar') || 'fr');
      navigate(constructUrlWithParams(`/${lang || 'fr'}${seoUrl}`, newParams));
    } else {
      navigate(constructUrlWithParams(`/${lang || 'fr'}/cars`, newParams));
    }
  }

  const filteredLocations = locations.filter(loc => normalizeText(loc.name).includes(normalizeText(locSearchTerm)));

  return (
    <form onSubmit={submitForm} className={cn("w-full", !isMobile && "max-w-[850px] mx-auto sm:w-[850px]")}>
      <div className={cn(
        "transition-shadow duration-300 ease-in-out",
        !isMobile && "md:whitespace-nowrap md:rounded-full rounded-2xl border border-border bg-background text-foreground shadow-xl shadow-neutral-900/10 dark:shadow-black/20 hover:shadow-lg hover:shadow-neutral-900/5 dark:hover:shadow-black/30",
        isMobile && "border-none shadow-none"
      )}>
        <div className={cn("relative flex flex-col md:items-center", !isMobile && "md:grid md:h-[var(--search-panel-height)] md:grid-cols-1")}>
          <div className={cn(
            "flex flex-col md:gap-0 gap-0 search-panel-grid",
            !isMobile && `md:grid md:h-full md:items-center md:justify-center ${mode === 'agencies' ? 'md:grid-cols-2' : 'md:grid-cols-3'} md:p-0 p-3`,
            isMobile && "p-0"
          )}>
            {/* First section: Location */}
            <div className={cn("relative w-full search-panel-section", !isMobile && "md:h-full h-auto md:min-w-[200px] mb-2 md:mb-0")}>
              <Separator
                orientation="vertical"
                className={cn(
                  "absolute right-0 top-1/2 -translate-y-1/2 h-6 shrink-0 hidden search-panel-separator",
                  !isMobile && `md:block ${mode === 'agencies' ? '' : 'ltr:hidden rtl:block'}`
                )}
              />
              {/* Mobile separator */}
              <Separator
                orientation="horizontal"
                className="md:hidden w-full mt-2"
              />
              <div className="flex md:size-full w-full md:items-center items-stretch justify-center px-0">
                {/* Mobile: Direct search input */}
                {isMobile ? (
                  <div className="w-full px-3 py-3 min-h-[56px] flex flex-col justify-center">
                    <div className="text-sm font-bold">
                      {mode === 'agencies' ? 
                        (lang === 'ar' ? 'موقع الوكالة' : 
                         lang === 'en' ? 'Agency Location' : 
                         'Localisation Agence') : 
                        t('cars.pickupDropoff')
                      }
                    </div>
                    <input
                      ref={mainSearchInputRef}
                      type="text"
                      style={{ 
                        border: 'none', 
                        outline: 'none', 
                        boxShadow: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        appearance: 'none',
                        fontSize: '16px'
                      }}
                      value={location ? (
                        // First try to find in static locations
                        locations.find((loc) => loc.slug === location)?.name ||
                        // If not found, use the location value as display name (for database cities)
                        location.charAt(0).toUpperCase() + location.slice(1).replace(/[-_]/g, ' ')
                      ) : locSearchTerm}
                      onChange={(e) => {
                        const value = e.target.value;
                        setLocSearchTerm(value);
                        // Clear location if user is typing
                        if (location && value !== (locations.find((loc) => loc.slug === location)?.name || location.charAt(0).toUpperCase() + location.slice(1).replace(/[-_]/g, ' '))) {
                          setLocation('');
                        }
                        // Auto-open search results when typing
                        if (value.trim()) {
                          setIsLocationSelectorOpen(true);
                          setIsCheckInOpen(false);
                          setIsCheckOutOpen(false);
                        } else {
                          setIsLocationSelectorOpen(false);
                        }
                      }}
                      onFocus={() => {
                        if (!location) {
                          setIsLocationSelectorOpen(true);
                          setIsCheckInOpen(false);
                          setIsCheckOutOpen(false);
                        }
                      }}
                      placeholder={mode === 'agencies' ?
                        (lang === 'ar' ? 'البحث عن مدينة' : 
                         lang === 'en' ? 'Search for a city' : 
                         'Rechercher une ville') :
                        (lang === 'ar' ? 'البحث عن موقع' : 
                         lang === 'en' ? 'Search for a location' : 
                         'Rechercher un lieu')
                      }
                      className="w-full bg-transparent text-base md:text-lg font-semibold text-foreground placeholder:text-muted-foreground !border-0 !outline-0 !ring-0 !shadow-none focus:!border-0 focus:!outline-0 focus:!ring-0 focus:!shadow-none active:!border-0 active:!outline-0 active:!ring-0 active:!shadow-none hover:!border-0 hover:!outline-0 !p-0 !m-0 -mt-1 !text-base"
                    />
                  </div>
                ) : (
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        role="combobox"
                        aria-expanded={open}
                        aria-label={mode === 'agencies' ? 
                          (lang === 'ar' ? 'اختر موقع الوكالة' : 
                           lang === 'en' ? 'Select agency location' : 
                           'Sélectionner la localisation de l\'agence') : 
                          t('cars.pickupDropoff', 'Prise en charge / Retour')}
                        onClick={() => setOpen(!open)}
                        className="md:size-full w-full h-auto flex-col overflow-hidden md:rounded-full rounded-xl border-none px-4 md:py-0 py-3 focus-visible:z-10 md:min-h-0 min-h-[56px] items-start"
                      >
                        <div className="flex size-full items-center justify-between">
                          <div className="flex size-full flex-col items-start rtl:items-end justify-center truncate">
                            <div className="text-sm md:text-base font-bold">
                              {mode === 'agencies' ? 
                                (lang === 'ar' ? 'موقع الوكالة' : 
                                 lang === 'en' ? 'Agency Location' : 
                                 'Localisation Agence') : 
                                t('cars.pickupDropoff')
                              }
                            </div>
                            {location ? (
                              <p className="truncate font-semibold text-foreground text-base md:text-lg -mt-2">
                                {
                                  // First try to find in static locations
                                  locations.find((loc) => loc.slug === location)?.name ||
                                  // If not found, use the location value as display name (for database cities)
                                  location.charAt(0).toUpperCase() + location.slice(1).replace(/[-_]/g, ' ')
                                }
                              </p>
                            ) : (
                              <div className="truncate text-muted-foreground text-base md:text-lg -mt-2">
                                {mode === 'agencies' ?
                                  (lang === 'ar' ? 'البحث عن مدينة' : 
                                   lang === 'en' ? 'Search for a city' : 
                                   'Rechercher une ville') :
                                  (lang === 'ar' ? 'البحث عن موقع' : 
                                   lang === 'en' ? 'Search for a location' : 
                                   'Rechercher un lieu')
                                }
                              </div>
                            )}
                          </div>
                          <SelectorIcon className="-mr-2 ml-2 rtl:-ml-2 rtl:mr-2 size-5 shrink-0 opacity-50" />
                        </div>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[400px]">
                    <div className="max-h-[360px] overflow-y-auto">
                      {/* Search input */}
                      <div className="flex items-center px-3 py-2 pb-0">
                        <SearchIcon className="mr-2 rtl:mr-0 rtl:ml-2 h-4 w-4 shrink-0 opacity-50" />
                        <input
                          type="text"
                          value={locSearchTerm}
                          onChange={(e) => setLocSearchTerm(e.target.value)}
                          placeholder={t('cars.searchLocation')}
                          className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 border-0 rounded-none px-0 focus:ring-0 focus:border-transparent focus:outline-none focus:shadow-none !border-0 !outline-0 !ring-0 !shadow-none !bg-transparent"
                        />
                      </div>
                      {/* When empty: show Popular Destinations grid (desktop only) */}
                      {locSearchTerm.trim() === '' ? (
                        <>
                          {/* Mobile: Simple search prompt */}
                          <div className="md:hidden px-3 py-4 text-center">
                            <p className="text-sm text-muted-foreground">
                              {lang === 'ar' ? 'اكتب لبدء البحث عن المدن' : 
                               lang === 'en' ? 'Type to search for cities' : 
                               'Tapez pour rechercher des villes'}
                            </p>
                          </div>
                          
                          {/* Desktop: Popular Destinations grid */}
                          <div className="pb-3 hidden md:block">
                            <div className="px-3 py-2 text-xs font-medium text-muted-foreground">{t('locations.popularLocations', 'Popular Destinations')}</div>
                            <div className="grid grid-cols-3 gap-3 px-3">
                            {citiesLoading ? (
                              // Loading skeleton
                              Array.from({ length: 6 }).map((_, index) => (
                                <div key={index} className="text-left rounded-lg overflow-hidden border border-border">
                                  <div className="h-20 bg-gray-200 dark:bg-muted animate-pulse" />
                                  <div className="p-2">
                                    <div className="h-3 bg-gray-200 dark:bg-muted rounded animate-pulse" />
                                  </div>
                                </div>
                              ))
                            ) : popularCities.length > 0 ? (
                              // Database cities
                              popularCities.map((city) => (
                                <button
                                  key={city.id}
                                  type="button"
                                  onClick={() => {
                                    const selectedValue = findLocationMatch(city.name)
                                    setLocation(selectedValue)
                                    setLocSearchTerm('')
                                    if (isMobile) {
                                      setIsLocationSelectorOpen(false)
                                      setIsCheckInOpen(true)
                                    } else {
                                      setOpen(false)
                                      setIsCheckInPopoverOpen(true)
                                    }
                                  }}
                                  className="text-left rounded-lg overflow-hidden border border-border hover:shadow-md transition-shadow"
                                >
                                  <div className="relative h-20">
                                    {city.image_url ? (
                                      <img 
                                        src={city.image_url} 
                                        alt={city.name} 
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          // Fallback to gradient background if image fails to load
                                          const target = e.target as HTMLImageElement;
                                          target.style.display = 'none';
                                          const parent = target.parentElement;
                                          if (parent) {
                                            parent.className += ' bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 flex items-center justify-center';
                                            parent.innerHTML = '<div class="text-center"><div class="text-2xl mb-1">🏙️</div><div class="text-xs font-medium text-blue-800 dark:text-blue-200">' + city.region + '</div></div>';
                                          }
                                        }}
                                      />
                                    ) : (
                                      <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 flex items-center justify-center">
                                        <div className="text-center">
                                          <div className="text-2xl mb-1">🏙️</div>
                                          <div className="text-xs font-medium text-blue-800 dark:text-blue-200">{city.region}</div>
                                        </div>
                                      </div>
                                    )}
                                    <div className="absolute inset-0 bg-black/20" />
                                  </div>
                                  <div className="p-2">
                                    <div className="text-xs font-medium truncate">{city.name}</div>
                                  </div>
                                </button>
                              ))
                            ) : (
                              // Fallback to static data if database fails
                              popularLocationsStatic.slice(0, 6).map((pl) => (
                                <button
                                  key={pl.id}
                                  type="button"
                                  onClick={() => {
                                    const match = locations.find(l => l.slug.toLowerCase() === pl.slug.toLowerCase() || l.name.toLowerCase() === pl.name.split(',')[0].toLowerCase())
                                    setLocation(match ? match.slug : pl.slug)
                                    setLocSearchTerm('')
                                    if (isMobile) {
                                      setIsLocationSelectorOpen(false)
                                      setIsCheckInOpen(true)
                                    } else {
                                      setOpen(false)
                                      setIsCheckInPopoverOpen(true)
                                    }
                                  }}
                                  className="text-left rounded-lg overflow-hidden border border-border hover:shadow-md transition-shadow"
                                >
                                  <div className="relative h-20">
                                    <img src={pl.imageUrl} alt={pl.name} className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/20" />
                                  </div>
                                  <div className="p-2">
                                    <div className="text-xs font-medium truncate">{pl.name}</div>
                                  </div>
                                </button>
                              ))
                            )}
                          </div>
                          </div>
                        </>
                      ) : (
                        // Show search results from database
                      <div className="py-1 pt-0">
                          {citySearchLoading ? (
                            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                              {t('common.searching', 'Searching...')}
                            </div>
                          ) : searchedCities.length > 0 ? (
                            <>
                              <div className="px-3 py-2 text-xs font-medium text-muted-foreground border-b border-border pt-0">
                                {t('locations.cities', 'Cities')}
                              </div>
                              {searchedCities.map((city) => (
                                <div
                                  key={city.id}
                                  className="relative flex cursor-pointer select-none items-center rounded-sm px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                                  onClick={() => {
                                    const selectedValue = findLocationMatch(city.name)
                                    setLocation(selectedValue)
                                    setLocSearchTerm('')
                                    if (isMobile) {
                                      setIsLocationSelectorOpen(false)
                                      setIsCheckInOpen(true)
                                    } else {
                                      setOpen(false)
                                      setIsCheckInPopoverOpen(true)
                                    }
                                  }}
                                >
                                  <CheckIcon
                                    className={cn(
                                      "mr-2 rtl:mr-0 rtl:ml-2 size-4 shrink-0",
                                      isCitySelected(city.name) ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  <div className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                    <div>
                                      <div className="font-medium">{city.name}</div>
                                      <div className="text-xs text-muted-foreground">{city.region}</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </>
                          ) : (
                            // Fallback to static locations if no cities found
                            <>
                              {locations
                                .filter((loc) => loc.name.toLowerCase().includes(locSearchTerm.toLowerCase()))
                                .slice(0, 10)
                                .map((loc) => (
                          <div
                            key={loc.id}
                            className="relative flex cursor-pointer select-none items-center rounded-sm px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                            onClick={() => {
                              setLocation(loc.slug)
                              setLocSearchTerm('')
                              if (isMobile) {
                                setIsLocationSelectorOpen(false)
                                setIsCheckInOpen(true)
                              } else {
                                setOpen(false)
                                setIsCheckInPopoverOpen(true)
                              }
                            }}
                          >
                            <CheckIcon
                              className={cn(
                                "mr-2 rtl:mr-0 rtl:ml-2 size-4 shrink-0",
                                        location === loc.slug ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {loc.name}
                          </div>
                        ))}
                              {locations.filter((loc) => loc.name.toLowerCase().includes(locSearchTerm.toLowerCase())).length === 0 && searchedCities.length === 0 && (
                                <div className="px-3 py-4 text-center text-sm text-muted-foreground">{t('locations.noResults', 'No locations found')}</div>
                              )}
                            </>
                          )}
                      </div>
                      )}
                    </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>

            {/* Mobile Inline Location Selector */}
            <AnimatePresence>
              {isMobile && isLocationSelectorOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 450, opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="overflow-hidden bg-background"
                >
                  <div className="h-full flex flex-col bg-background">
                    {/* Content */}
                    <div className="flex-grow overflow-y-auto bg-background">
                      {locSearchTerm.trim() === '' ? (
                        <>
                          {/* Mobile: Popular Destinations grid */}
                          <div className="pb-3 bg-background">
                            <div className="px-3 py-2 text-xs font-medium text-muted-foreground bg-background">{t('locations.popularLocations', 'Popular Destinations')}</div>
                            <div className="flex gap-3 overflow-x-auto pb-2 px-3 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                              {citiesLoading ? (
                                // Loading skeleton
                                Array.from({ length: 4 }).map((_, index) => (
                                  <div key={index} className="text-left rounded-lg overflow-hidden border border-border flex-shrink-0 w-36">
                                    <div className="h-20 bg-gray-200 dark:bg-muted animate-pulse" />
                                    <div className="p-2">
                                      <div className="h-3 bg-gray-200 dark:bg-muted rounded animate-pulse" />
                                    </div>
                                  </div>
                                ))
                              ) : popularCities.length > 0 ? (
                                // Database cities
                                popularCities.map((city) => (
                                  <button
                                    key={city.id}
                                    type="button"
                                    onClick={() => {
                                      const selectedValue = findLocationMatch(city.name)
                                      setLocation(selectedValue)
                                      setIsLocationSelectorOpen(false)
                                      setLocSearchTerm('')
                                      if (isMobile) {
                                        setIsCheckInOpen(true)
                                      } else {
                                        setOpen(false)
                                        setIsCheckInPopoverOpen(true)
                                      }
                                    }}
                                    className="text-left rounded-lg overflow-hidden border border-border hover:shadow-md transition-shadow flex-shrink-0 w-36"
                                  >
                                    <div className="relative h-20">
                                      {city.image_url ? (
                                        <img src={city.image_url} alt={city.name} className="w-full h-full object-cover" />
                                      ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 flex items-center justify-center">
                                          <div className="text-center">
                                            <div className="text-2xl mb-1">🏙️</div>
                                            <div className="text-xs font-medium text-blue-800 dark:text-blue-200">{city.region}</div>
                                          </div>
                                        </div>
                                      )}
                                      <div className="absolute inset-0 bg-black/20" />
                                    </div>
                                    <div className="p-2 bg-background">
                                      <div className="text-xs font-medium truncate text-foreground">{city.name}</div>
                                    </div>
                                  </button>
                                ))
                              ) : (
                                // Fallback to static data if database fails
                                popularLocationsStatic.slice(0, 4).map((pl) => (
                                  <button
                                    key={pl.id}
                                    type="button"
                                    onClick={() => {
                                      const match = locations.find((l) => l.slug.toLowerCase() === pl.slug.toLowerCase() || l.name.toLowerCase() === pl.name.split(',')[0].toLowerCase())
                                      setLocation(match ? match.slug : pl.slug)
                                      setIsLocationSelectorOpen(false)
                                      setLocSearchTerm('')
                                      if (isMobile) {
                                        setIsCheckInOpen(true)
                                      } else {
                                        setOpen(false)
                                        setIsCheckInPopoverOpen(true)
                                      }
                                    }}
                                    className="text-left rounded-lg overflow-hidden border border-border hover:shadow-md transition-shadow flex-shrink-0 w-36"
                                  >
                                    <div className="relative h-20">
                                      <img src={pl.imageUrl} alt={pl.name} className="w-full h-full object-cover" />
                                      <div className="absolute inset-0 bg-black/20" />
                                    </div>
                                    <div className="p-2 bg-background">
                                      <div className="text-xs font-medium truncate text-foreground">{pl.name}</div>
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        </>
                      ) : (
                        // Show search results from database
                        <div className="py-1 pt-0 bg-background">
                          {citySearchLoading ? (
                            <div className="px-3 py-4 text-center text-sm text-muted-foreground bg-background">{t('common.searching', 'Searching...')}</div>
                          ) : searchedCities.length > 0 ? (
                            <>
                              <div className="px-3 py-2 text-xs font-medium text-muted-foreground border-b border-border pt-0">
                                {t('locations.cities', 'Cities')}
                              </div>
                              {searchedCities.map((city) => (
                                <div
                                  key={city.id}
                                  className="relative flex cursor-pointer select-none items-center rounded-sm px-3 py-2 text-sm bg-background text-foreground hover:bg-accent hover:text-accent-foreground"
                                  onClick={() => {
                                    const selectedValue = findLocationMatch(city.name)
                                    setLocation(selectedValue)
                                    setIsLocationSelectorOpen(false)
                                    setLocSearchTerm('')
                                    if (isMobile) {
                                      setIsCheckInOpen(true)
                                    } else {
                                      setOpen(false)
                                      setIsCheckInPopoverOpen(true)
                                    }
                                  }}
                                >
                                  <CheckIcon
                                    className={cn('mr-2 rtl:mr-0 rtl:ml-2 size-4 shrink-0 text-foreground', isCitySelected(city.name) ? 'opacity-100' : 'opacity-0')}
                                  />
                                  <div className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                    <div>
                                      <div className="font-medium text-foreground">{city.name}</div>
                                      <div className="text-xs text-muted-foreground">{city.region}</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </>
                          ) : (
                            // Fallback to static locations if no cities found
                            <>
                              {locations
                                .filter((loc) => loc.name.toLowerCase().includes(locSearchTerm.toLowerCase()))
                                .slice(0, 10)
                                .map((loc) => (
                                    <div
                                      key={loc.id}
                                      className="relative flex cursor-pointer select-none items-center rounded-sm px-3 py-2 text-sm bg-background text-foreground hover:bg-accent hover:text-accent-foreground"
                                      onClick={() => {
                                        setLocation(loc.slug)
                                        setIsLocationSelectorOpen(false)
                                        setLocSearchTerm('')
                                        if (isMobile) {
                                          setIsCheckInOpen(true)
                                        } else {
                                          setOpen(false)
                                          setIsCheckInPopoverOpen(true)
                                        }
                                      }}
                                    >
                                      <CheckIcon
                                        className={cn('mr-2 rtl:mr-0 rtl:ml-2 size-4 shrink-0 text-foreground', location === loc.slug ? 'opacity-100' : 'opacity-0')}
                                      />
                                      <span className="text-foreground">{loc.name}</span>
                                    </div>
                                  ))}
                                {locations.filter((loc) => loc.name.toLowerCase().includes(locSearchTerm.toLowerCase())).length === 0 &&
                                  searchedCities.length === 0 && (
                                    <div className="px-3 py-4 text-center text-sm text-muted-foreground bg-background">{t('locations.noResults', 'No locations found')}</div>
                                  )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Second section: Check-in (cars mode) or Agency Name Search (agencies mode) */}
            {mode === 'agencies' ? (
              <div className={cn("w-full search-panel-section", !isMobile && "md:h-full h-auto md:min-w-[200px] mb-2 md:mb-0")}>
                {/* Mobile separator */}
                <Separator
                  orientation="horizontal"
                  className="md:hidden w-full mb-2"
                />
                <div className="flex md:size-full w-full md:items-center items-stretch justify-center px-0">
                  <Popover open={agencyOpen} onOpenChange={setAgencyOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        role="combobox"
                        aria-expanded={agencyOpen}
                        className="md:size-full w-full h-auto flex-col overflow-hidden md:rounded-full rounded-xl border-none md:py-0 py-3 pl-4 pr-12 rtl:pl-12 rtl:pr-4 focus-visible:z-10 md:min-h-0 min-h-[56px]"
                      >
                        <div className="flex size-full items-center justify-between">
                          <div className="flex size-full flex-col items-start rtl:items-end justify-center truncate">
                            <div className="text-sm md:text-base font-bold">
                              {lang === 'ar' ? 'اسم الوكالة' : 
                               lang === 'en' ? 'Agency Name' : 
                               'Nom de l\'Agence'}
                            </div>
                            {selectedAgency ? (
                              <p className="truncate font-bold text-sm md:text-base -mt-2 text-foreground">
                                {agencies.find(agency => agency.id === selectedAgency)?.name}
                              </p>
                            ) : (
                              <div className="text-muted-foreground text-sm md:text-base -mt-2">
                                {lang === 'ar' ? 'اختر الوكالة' : 
                                 lang === 'en' ? 'Select Agency' : 
                                 'Choisir Agence'}
                              </div>
                            )}
                          </div>
                          <SelectorIcon className="ml-2 rtl:-ml-2 rtl:mr-2 size-5 shrink-0 opacity-50 mr-4" />
                        </div>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]">
                      <div className="max-h-[300px] overflow-y-auto">
                        <div className="flex items-center px-3 py-2 pb-0">
                          <SearchIcon className="mr-2 rtl:mr-0 rtl:ml-2 h-4 w-4 shrink-0 opacity-50" />
                          <input
                            type="text"
                            placeholder={lang === 'ar' ? 'البحث عن وكالة...' : 
                                       lang === 'en' ? 'Search agency...' : 
                                       'Rechercher agence...'}
                            value={agencySearchTerm}
                            onChange={(e) => setAgencySearchTerm(e.target.value)}
                            className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 border-0 rounded-none px-0 focus:ring-0 focus:border-transparent focus:outline-none focus:shadow-none !border-0 !outline-0 !ring-0 !shadow-none !bg-transparent"
                          />
                        </div>
                        <div className="py-1 pt-0">
                          {/* Show filtered agencies or top 10 if no search */}
                          {agencies
                            .filter(agency => 
                              agencySearchTerm === '' || 
                              agency.name.toLowerCase().includes(agencySearchTerm.toLowerCase())
                            )
                            .slice(0, 10) // Show only 10 agencies
                            .map((agency) => (
                            <div
                              key={agency.id}
                              className="relative flex cursor-pointer select-none items-center rounded-sm px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                              onClick={() => {
                                setSelectedAgency(agency.id)
                                setAgencyOpen(false)
                                setAgencySearchTerm('')
                              }}
                            >
                              <CheckIcon
                                className={cn(
                                  "mr-2 rtl:mr-0 rtl:ml-2 size-4 shrink-0",
                                  selectedAgency === agency.id
                                    ? "opacity-100"
                                    : "opacity-0"
                                )}
                              />
                              {agency.name}
                            </div>
                          ))}
                          {/* No results message */}
                          {agencies.filter(agency => 
                            agencySearchTerm === '' || 
                            agency.name.toLowerCase().includes(agencySearchTerm.toLowerCase())
                          ).length === 0 && agencySearchTerm && (
                            <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                              {lang === 'ar' ? 'لم يتم العثور على وكالات' : 
                               lang === 'en' ? 'No agencies found' : 
                               'Aucune agence trouvée'}
                            </div>
                          )}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            ) : (
              <>
                <div className={cn("relative w-full search-panel-section", !isMobile && "md:h-full h-auto md:min-w-[150px] mb-2 md:mb-0")}>
                  <Separator
                    orientation="vertical"
                    className={cn("absolute right-0 top-1/2 -translate-y-1/2 h-6 shrink-0 hidden", !isMobile && "md:block")}
                  />
                  {/* Mobile separator */}
                  <Separator
                    orientation="horizontal"
                    className="md:hidden w-full mb-2"
                  />
                  <div className="flex md:size-full w-full md:items-center items-stretch justify-center px-0">
                    <Popover open={!isMobile && isCheckInPopoverOpen} onOpenChange={!isMobile ? setIsCheckInPopoverOpen : () => {}}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant={"ghost"}
                          onClick={() => {
                            if (isMobile) {
                              setIsCheckInOpen(!isCheckInOpen)
                              setIsLocationSelectorOpen(false)
                              setIsCheckOutOpen(false)
                            } else {
                              setIsCheckInPopoverOpen(!isCheckInPopoverOpen)
                            }
                          }}
                          className="md:size-full w-full h-auto flex-col overflow-hidden md:rounded-full rounded-xl border-none px-4 md:py-0 py-3 focus-visible:z-10 md:min-h-0 min-h-[56px]"
                        >
                          <div className="flex size-full flex-col items-start rtl:items-end justify-center truncate">
                            <div className="text-sm md:text-base font-bold">{t('cars.checkIn')}</div>
                            {checkInDate ? (
                              <div className="font-semibold text-foreground text-base md:text-lg -mt-2">
                                {format(checkInDate, "LLL dd, y")}
                              </div>
                            ) : (
                              <div className="truncate text-muted-foreground text-base md:text-lg -mt-2">
                                {t('cars.pickDate')}
                              </div>
                            )}
                          </div>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={checkInDate}
                          onSelect={(date) => {
                            if (checkOutDate && date && isAfter(date, checkOutDate)) {
                              setCheckOutDate(undefined)
                            }
                            setCheckInDate(date)
                            if (isMobile) {
                              setIsCheckInOpen(false)
                              setIsCheckOutOpen(true)
                            } else {
                              setIsCheckInPopoverOpen(false)
                              setIsCheckOutPopoverOpen(true)
                            }
                          }}
                          initialFocus
                          disabled={(date) => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <AnimatePresence>
                    {isMobile && isCheckInOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden max-h-80"
                      >
                        <Calendar
                          mode="single"
                          selected={checkInDate}
                          onSelect={(date) => {
                            if (checkOutDate && date && isAfter(date, checkOutDate)) {
                              setCheckOutDate(undefined)
                            }
                            setCheckInDate(date)
                            setIsCheckInOpen(false)
                            setIsCheckOutOpen(true)
                          }}
                          initialFocus
                          disabled={(date) => date < new Date()}
                          className={cn(isMobile && "mx-auto")}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <Separator
                    orientation="vertical"
                    className={cn("absolute left-0 top-1/2 -translate-y-1/2 h-6 shrink-0 hidden", !isMobile && "md:block")}
                  />
                </div>
                {/* Third section: Check-out (no separator) */}
                <div className={cn("w-full search-panel-section", !isMobile && "md:h-full h-auto md:min-w-[150px] mb-2 md:mb-0")}>
                  {/* Mobile separator */}
                  <Separator
                    orientation="horizontal"
                    className="md:hidden w-full mb-2"
                  />
                  <div className="flex md:size-full w-full md:items-center items-stretch justify-center px-0">
                    <Popover open={!isMobile && isCheckOutPopoverOpen} onOpenChange={!isMobile ? setIsCheckOutPopoverOpen : () => {}}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant={"ghost"}
                          onClick={() => {
                            if (isMobile) {
                              setIsCheckOutOpen(!isCheckOutOpen)
                              setIsLocationSelectorOpen(false)
                              setIsCheckInOpen(false)
                            } else {
                              setIsCheckOutPopoverOpen(!isCheckOutPopoverOpen)
                            }
                          }}
                          className="md:size-full w-full h-auto flex-col overflow-hidden md:rounded-full rounded-xl border-none md:py-0 py-3 pl-4 pr-12 rtl:pl-12 rtl:pr-4 focus-visible:z-10 md:min-h-0 min-h-[56px]"
                        >
                          <div className="flex size-full flex-col items-start rtl:items-end justify-center truncate">
                            <div className="text-sm md:text-base font-bold">{t('cars.checkOut')}</div>
                            {checkOutDate ? (
                              <div className="font-semibold text-foreground text-base md:text-lg -mt-2">
                                {format(checkOutDate, "LLL dd, y")}
                              </div>
                            ) : (
                              <div className="truncate text-muted-foreground text-base md:text-lg -mt-2">
                                {t('cars.pickDate')}
                              </div>
                            )}
                          </div>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="range"
                          selected={{ from: checkInDate, to: checkOutDate }}
                          onSelect={(range) => {
                            if (range?.from) setCheckInDate(range.from)
                            if (range?.to) {
                              setCheckOutDate(range.to)
                              setIsCheckOutPopoverOpen(false)
                            } else {
                              setCheckOutDate(undefined)
                            }
                          }}
                          initialFocus
                          disabled={{ before: checkInDate ? addDays(checkInDate, 1) : addDays(new Date(), 1) }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <AnimatePresence>
                    {isMobile && isCheckOutOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden max-h-80"
                      >
                        <Calendar
                          mode="range"
                          selected={{ from: checkInDate, to: checkOutDate }}
                          onSelect={(range) => {
                            if (range?.from) setCheckInDate(range.from)
                            if (range?.to) {
                              setCheckOutDate(range.to)
                              setIsCheckOutOpen(false)
                            } else {
                              setCheckOutDate(undefined)
                            }
                          }}
                          initialFocus
                          disabled={{ before: checkInDate ? addDays(checkInDate, 1) : addDays(new Date(), 1) }}
                          className={cn(isMobile && "mx-auto")}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            )}
          </div>
          {/* Desktop: Original circular button */}
          <div className={cn("z-20 search-panel-button", !isMobile && "absolute right-2 rtl:right-auto rtl:left-2")}>
            <Button
              type="submit"
              variant={isMobile ? "default" : undefined}
              className={cn(
                !isMobile && "flex size-[calc(var(--search-panel-height)_-_1.25rem)] shrink-0 items-center justify-center rounded-full bg-foreground text-background hover:bg-foreground/90",
                isMobile && "w-full h-14 rounded-full text-lg font-bold shadow-lg mt-4"
              )}
            >
              {isMobile && <Search className="mr-3 h-6 w-6" />}
              <span className={cn(isMobile ? "" : "sr-only")}>{t('cars.search', 'Search')}</span>
              {!isMobile && <SearchIcon className="size-[calc((var(--search-panel-height)_-_1.25rem)/2.66)] shrink-0 [stroke-width:3px]" />}
            </Button>
          </div>
          
        </div>
      </div>
    </form>
  )
}
